import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import axios from "axios";
import { BASE_API_URL } from "../Constant";
import { getPermissionsData } from "../Redux/UserMaintenance";

function Logs() {
  const [selectedCourse, setSelectedCourse] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const { permissionsData } = useSelector((state) => state.userMaintenance);

  const { instance } = useMsal();
  const activeUser = instance.getActiveAccount()?.idTokenClaims?.enterpriseID;

  const empData = permissionsData?.find(
    (permission) => permission.empId === activeUser,
  );

  const matchingArr = [...new Set(empData?.permissions?.split(",") || [])];

  useEffect(() => {
    dispatch(getPermissionsData());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCourse) return;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_API_URL}/api/update-logs`, {
          params: { course: selectedCourse },
        });
        setLogs(res.data.logs);
        setError("");
      } catch (err) {
        setError("Failed to load logs");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [selectedCourse]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString("en-GB");
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    try {
      const [h, m, s] = timeStr.split(":");
      const d = new Date();
      d.setHours(h, m, s);
      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return timeStr;
    }
  };

  return (
    <Box
      sx={{
        p: 4,
        display: "flex",
        justifyContent: "center",
        backgroundColor: "#f5f6fa",
        minHeight: "100vh",
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: 4,
          borderRadius: 3,
          width: "100%",
          maxWidth: "1100px",
        }}
      >
        <Typography variant="h4" fontWeight="bold" mb={3}>
          Course Update Logs
        </Typography>

        {/* Dropdown */}
        <FormControl fullWidth sx={{ maxWidth: 350, mb: 3 }}>
          <InputLabel>Select Course</InputLabel>
          <Select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            label="Select Course"
            sx={{ borderRadius: 2 }}
          >
            {matchingArr.map(
              (option) =>
                option && (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ),
            )}
          </Select>
        </FormControl>

        {/* States */}
        {!selectedCourse ? (
          <Alert severity="info">Please select a course to view logs.</Alert>
        ) : loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : logs.length === 0 ? (
          <Alert severity="info">No logs found.</Alert>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: "linear-gradient(90deg, #6a11cb, #2575fc)",
                  }}
                >
                  {["#", "Employee ID", "Date", "Time", "File"].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "0.95rem",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {logs.map((log, index) => (
                  <TableRow
                    key={log.id || index}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f3f6ff",
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{log.empId}</TableCell>
                    <TableCell>{formatDate(log.updated_date)}</TableCell>
                    <TableCell>{formatTime(log.updated_time)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloudDownloadIcon />}
                        href={`${BASE_API_URL}/uploads/${log.uploaded_file_name}`}
                        target="_blank"
                      >
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default Logs;
